import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, FileDown, Calendar, DollarSign, PieChart, BarChart3 } from 'lucide-react';

const CompanyPnL = () => {
  const [pnlData, setPnlData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchPnL();
  }, [selectedYear]);

  const fetchPnL = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/company/pnl?year=${selectedYear}`);
      setPnlData(response.data);
    } catch (error) {
      toast.error('Failed to fetch P&L data');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    // TODO: Implement PDF export
    toast.info('PDF export coming soon');
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  const summary = pnlData?.summary || {};
  const cycles = pnlData?.cycles || [];
  const monthly = pnlData?.monthly || [];

  return (
    <div className="p-8" data-testid="company-pnl-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Firm Performance</p>
            <h1 className="cb-h1">Company P&amp;L</h1>
            <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)' }}>Profit &amp; Loss Summary</p>
          </div>
          <div className="flex gap-3 items-center">
            <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
              <Calendar className="w-5 h-5" style={{ color: 'var(--cb-ink-soft)' }} />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                data-testid="year-selector"
                className="form-input"
                style={{ width: '8rem' }}
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button
              onClick={downloadPDF}
              data-testid="download-pdf-btn"
              className="cb-btn-secondary"
            >
              <FileDown className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="cb-card" data-testid="profit-made-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Profit Made</p>
            <TrendingUp className="w-5 h-5 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-card-value--sm cb-num">{summary.total_profit_made_usdt?.toLocaleString() || '0'}</p>
          <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>USDT</p>
        </div>

        <div className="cb-card" data-testid="distributed-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Distributed</p>
            <DollarSign className="w-5 h-5 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-card-value--sm cb-num">-{summary.total_distributed_usdt?.toLocaleString() || '0'}</p>
          <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>USDT to clients</p>
        </div>

        <div className="cb-card" data-testid="expenses-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Expenses</p>
            <PieChart className="w-5 h-5 cb-card-icon" />
          </div>
          <p className="cb-card-value cb-card-value--sm cb-num">-{summary.total_expenses_usdt?.toLocaleString() || '0'}</p>
          <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>USDT ({pnlData?.expenses_breakdown?.count || 0} items)</p>
        </div>

        <div className="cb-card" data-testid="gross-retention-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Gross Retention</p>
            <BarChart3 className="w-5 h-5 cb-card-icon" />
          </div>
          <p className={`cb-card-value cb-card-value--sm cb-num ${summary.gross_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
            {summary.gross_retention_usdt?.toLocaleString() || '0'}
          </p>
          <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>Before expenses</p>
        </div>

        {/* FX Gain/Loss card */}
        <div
          className="cb-card"
          data-testid="fx-gain-loss-card"
          title="FX gain/loss from adhoc USDT payouts (rate decision variance)"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">FX Gain / Loss</p>
            {(summary.fx_net_usdt || 0) >= 0 ? (
              <TrendingUp className="w-5 h-5 cb-card-icon" />
            ) : (
              <TrendingDown className="w-5 h-5 cb-card-icon" />
            )}
          </div>
          <p className={`cb-card-value cb-card-value--sm cb-num ${(summary.fx_net_usdt || 0) >= 0 ? 'cb-positive' : 'cb-negative'}`}>
            {(summary.fx_net_usdt || 0) >= 0 ? '+' : ''}{(summary.fx_net_usdt || 0).toLocaleString()}
          </p>
          <p className="cb-muted cb-num" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>
            From {summary.fx_gain_usdt ? `+${summary.fx_gain_usdt.toLocaleString()}` : '0'} gain · {summary.fx_loss_usdt ? `-${summary.fx_loss_usdt.toLocaleString()}` : '0'} loss
          </p>
        </div>

        <div className="cb-card" data-testid="net-retention-card">
          <div className="flex items-center justify-between mb-2">
            <p className="cb-card-label">Net Retention</p>
            {summary.net_retention_usdt >= 0 ? (
              <TrendingUp className="w-5 h-5 cb-card-icon" />
            ) : (
              <TrendingDown className="w-5 h-5 cb-card-icon" />
            )}
          </div>
          <p className={`cb-card-value cb-card-value--sm cb-num ${summary.net_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
            {summary.net_retention_usdt?.toLocaleString() || '0'}
          </p>
          <p className={`${summary.net_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`} style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)', fontWeight: 500 }}>
            {summary.net_retention_usdt >= 0 ? '✓ Surplus' : '⚠ Net Loss'}
          </p>
        </div>
      </div>

      {/* Monthly Trend */}
      {monthly.length > 0 && (
        <div className="cb-card mb-8" data-testid="monthly-trend">
          <h2 className="cb-section-title mb-4 flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
            <BarChart3 className="w-5 h-5 cb-card-icon" />
            Monthly Trend
          </h2>
          <div className="overflow-x-auto">
            <div className="flex min-w-max pb-4" style={{ gap: 'var(--cb-space-4)' }}>
              {monthly.map((m) => (
                <div key={m.month} className="cb-card" style={{ width: '8rem', padding: 'var(--cb-space-3)', boxShadow: 'none', textAlign: 'center' }}>
                  <p style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--cb-ink)' }}>{m.month}</p>
                  <div style={{ marginTop: 'var(--cb-space-2)', fontSize: '0.75rem' }}>
                    <div className="flex justify-between">
                      <span className="cb-muted">Made:</span>
                      <span className="cb-num" style={{ color: 'var(--cb-ink)' }}>{m.profit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between" style={{ marginTop: '2px' }}>
                      <span className="cb-muted">Dist:</span>
                      <span className="cb-num" style={{ color: 'var(--cb-ink)' }}>-{m.distributed.toLocaleString()}</span>
                    </div>
                    <div className={`flex justify-between ${m.retention >= 0 ? 'cb-positive' : 'cb-negative'}`} style={{ paddingTop: '4px', marginTop: '4px', borderTop: '1px solid var(--cb-line)', fontWeight: 500 }}>
                      <span>Net:</span>
                      <span className="cb-num">{m.retention >= 0 ? '+' : ''}{m.retention.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cycle Breakdown Table */}
      <div className="cb-card" data-testid="cycle-breakdown" style={{ padding: 'var(--cb-space-5)' }}>
        <h2 className="cb-section-title mb-4">Cycle Breakdown</h2>
        {cycles.length === 0 ? (
          <div className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>
            No closed cycles for {selectedYear}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="pnl-cycles-table" className="cb-table" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Type</th>
                  <th>Closed Date</th>
                  <th className="cb-num">AUM (USDT)</th>
                  <th className="cb-num">Profit Made</th>
                  <th className="cb-num">Distributed</th>
                  <th className="cb-num">Retention</th>
                  <th className="cb-num">Client Rate</th>
                  <th className="cb-num">Company Rate</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td className="capitalize cb-muted" style={{ fontSize: '0.8125rem' }}>{c.type?.replace('_', ' ')}</td>
                    <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>{c.closed_at ? new Date(c.closed_at).toLocaleDateString() : '-'}</td>
                    <td className="cb-num">{c.aum_usdt?.toLocaleString() || '0'}</td>
                    <td className="cb-num">{c.profit_made_usdt?.toLocaleString() || '0'}</td>
                    <td className="cb-num">-{c.distributed_usdt?.toLocaleString() || '0'}</td>
                    <td className={`cb-num ${c.retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`} style={{ fontWeight: 500 }}>
                      {c.retention_usdt?.toLocaleString() || '0'}
                    </td>
                    <td className="cb-num">{c.effective_client_rate?.toFixed(2) || '0'}%</td>
                    <td className={`cb-num ${c.effective_company_rate >= 0 ? 'cb-positive' : 'cb-negative'}`}>
                      {c.effective_company_rate?.toFixed(2) || '0'}%
                    </td>
                    <td>
                      {c.status === 'surplus' ? (
                        <span className="cb-pill cb-pill--positive">✓ Surplus</span>
                      ) : (
                        <span className="cb-pill cb-pill--negative">⚠ Loss</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--cb-hover)', fontWeight: 600 }}>
                  <td colSpan="4">TOTAL ({cycles.length} cycles)</td>
                  <td className="cb-num">{summary.total_profit_made_usdt?.toLocaleString()}</td>
                  <td className="cb-num">-{summary.total_distributed_usdt?.toLocaleString()}</td>
                  <td className={`cb-num ${summary.gross_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
                    {summary.gross_retention_usdt?.toLocaleString()}
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPnL;
